import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { db } from '../../services/db';
import { SearchFilters } from '../../services/repository';
import { ListingStatus } from '../../types';

export const listingKeys = {
    all: ['listings'] as const,
    lists: () => [...listingKeys.all, 'list'] as const,
    list: (filters: SearchFilters | undefined) => [...listingKeys.lists(), { filters }] as const,
    details: () => [...listingKeys.all, 'detail'] as const,
    detail: (id: string) => [...listingKeys.details(), id] as const,
    myListings: (userId: string) => [...listingKeys.all, 'my', userId] as const,
    admin: () => [...listingKeys.all, 'admin'] as const,
    // Under `all` on purpose: invalidating listingKeys.all (AddListing.tsx
    // does, on every save) prefix-matches this too, via React Query's default
    // fuzzy key matching. useHomepageListings used to key itself as the
    // unrelated literal ['homepage-listings'] — no mutation ever invalidated
    // it, so a host who just added, edited, paused or deleted a listing saw
    // the homepage's 5-minute-stale snapshot from before the change until the
    // cache happened to expire.
    homepage: () => [...listingKeys.all, 'homepage'] as const,
};

export function useListings(filters?: SearchFilters) {
    return useQuery({
        queryKey: listingKeys.list(filters),
        queryFn: () => db.getListings(filters),
    });
}

export function useListing(id?: string) {
    return useQuery({
        queryKey: listingKeys.detail(id!),
        queryFn: () => db.getListingById(id!),
        enabled: !!id,
    });
}

export function useMyListings(userId?: string | null) {
    return useQuery({
        queryKey: listingKeys.myListings(userId || ''),
        queryFn: () => db.getMyListings(userId!),
        enabled: !!userId,
    });
}

export function useAdminListings(isAdmin: boolean) {
    return useQuery({
        queryKey: listingKeys.admin(),
        queryFn: () => db.getAdminListings(),
        enabled: isAdmin,
    });
}

export function useUpdateListingStatus() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, status }: { id: string; status: ListingStatus }) =>
            db.updateListingStatus(id, status),
        onSuccess: (_, variables) => {
            // Invalidate the specific listing and the lists
            queryClient.invalidateQueries({ queryKey: listingKeys.detail(variables.id) });
            queryClient.invalidateQueries({ queryKey: listingKeys.lists() });
            queryClient.invalidateQueries({ queryKey: listingKeys.homepage() });
        },
    });
}

export function useDeleteListing() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => db.deleteListing(id),
        onSuccess: (_, id) => {
            queryClient.removeQueries({ queryKey: listingKeys.detail(id) });
            queryClient.invalidateQueries({ queryKey: listingKeys.lists() });
            queryClient.invalidateQueries({ queryKey: listingKeys.homepage() });
        },
    });
}
